import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Col,
  Row,
  Card,
  CardBody,
  CardHeader,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Container,
  Collapse,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import TableSearch from "../Common/TableSearch";
import CustomDataTable from "../CustomTable/CustomTable";
import BreadCrumb from "../Common/BreadCrumb";
import useTableSearch from "../../hooks/useTableSearch";
import useIsSmallScreen from "../../OrderingSystem/Payment Type/Component/small screen/SmallScreen";
import DeleteModal from "../../Components/Common/DeleteModal";
import Select from "react-select";

const ResponsiveDataManagement = ({
  title = "",
  mobileCardTitle = "",
  totalRows = 0,
  pageTitle = "",
  data = [],
  columns = [],
  stats = [],
  searchKeys = [],
  onAdd = null,
  onEdit = null,
  onView = null,
  onDelete = null,
  onSelect = null,
  selectOptions = [],
  selectValue = "",
  selectPlaceholder = "Filter...",
  selectLabel = "",
  showStats = true,
  statsCollapsible = true,
  loading = false,
  mobileCardRenderer = null,
  tabletCardRenderer = null,
  customActions = [],
  emptyStateMessage = "No data found",
  emptyStateIcon = "ri-emotion-unhappy-line",
  showBreadcrumb = true,
  showSearch = true,
  customStyles = {},
  onPaginationChange = null,
  onSearch = undefined,
  searchTerm: externalSearchTerm = undefined,
  customHeaderContent = null,
  mobileFilterButton = null,
  currentPage,
  pageSize,
}) => {
  // const [currentPage, setCurrentPage] = useState(1);
  // const [perPage, setPerPage] = useState(10);
  const [isStatsOpen, setIsStatsOpen] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const isSmallScreen = useIsSmallScreen();

  const {
    searchTerm: internalSearchTerm,
    setSearchTerm: setInternalSearchTerm,
    filteredData: internalFilteredData,
  } = useTableSearch(data, searchKeys);

  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const isServerSideSearch = typeof onSearch === "function";

  const effectiveSearchTerm = isServerSideSearch
    ? externalSearchTerm !== undefined
      ? externalSearchTerm
      : localSearchTerm
    : internalSearchTerm;

  const filteredData = isServerSideSearch ? data : internalFilteredData;

  const defaultCustomStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const tableStyles = { ...defaultCustomStyles, ...customStyles };

  const handleAdd = () => {
    if (onAdd) onAdd();
  };

  const handleEdit = (item) => {
    if (onEdit) onEdit(item);
  };

  const handleView = (item) => {
    if (onView) onView(item);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (onDelete && selectedItem) {
      onDelete(selectedItem);
    }
    setDeleteModal(false);
    setSelectedItem(null);
  };

  const handlePaginationChange = (page) => {
    onPaginationChange?.({ page, perPage: pageSize });
  };

  const handleRowsPerPageChange = (newPerPage) => {
    onPaginationChange?.({ page: 1, perPage: newPerPage });
  };

  const StatCard = ({ title, value, color, icon }) => (
    <Card className="shadow-sm border-0">
      <CardBody className="d-flex align-items-center justify-content-between">
        <div className="w-100">
          <h6 className="fw-semibold text-uppercase" style={{ color }}>
            {title}
          </h6>
          <div className="d-flex justify-content-between w-100">
            <h4 className="fs-2 fw-bold" style={{ color }}>
              {value}
            </h4>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                backgroundColor: color + "20",
                color: color,
                width: "36px",
                height: "36px",
              }}
            >
              <i className={`${icon} fs-5`} />
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  const MobileStatCard = ({ title, value, color = "#3B82F6", icon }) => {
    return (
      <div
        className="card border-0 shadow-sm rounded-2"
        style={{ marginBottom: "12px" }}
      >
        <div
          className="card-body p-3"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          <div style={{ flex: 1 }}>
            <h6
              className="text-uppercase fw-600 mb-1"
              style={{
                color: color,
                fontSize: "0.65rem",
                letterSpacing: "0.3px",
                margin: 0,
                fontWeight: 600,
              }}
            >
              {title}
            </h6>
            <h4
              className="fw-bold"
              style={{
                color: color,
                fontSize: "1.5rem",
                margin: 0,
              }}
            >
              {value}
            </h4>
          </div>

          {icon && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                backgroundColor: color + "15",
                color: color,
                flexShrink: 0,
                marginTop: "2px",
                fontSize: "18px",
              }}
            >
              <i className={icon}></i>
            </div>
          )}
        </div>
      </div>
    );
  };

  const ActionDropdown = ({ item }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    return (
      <Dropdown
        isOpen={dropdownOpen}
        toggle={() => setDropdownOpen(!dropdownOpen)}
        direction="down"
      >
        <DropdownToggle
          color="light"
          className="btn-sm shadow-sm"
          style={{ borderRadius: "50%" }}
          onClick={(e) => e.stopPropagation()}
        >
          <i className="ri-more-2-fill"></i>
        </DropdownToggle>
        <DropdownMenu end>
          {onView && (
            <DropdownItem
              onClick={(e) => {
                e.stopPropagation();
                handleView(item);
                setDropdownOpen(false);
              }}
            >
              <i className="bx bx-show me-2"></i>View
            </DropdownItem>
          )}

          {onEdit && (
            <DropdownItem
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(item);
                setDropdownOpen(false);
              }}
            >
              <i className="ri-edit-line text-warning me-2"></i>Edit
            </DropdownItem>
          )}

          {customActions?.map((action, idx) => (
            <DropdownItem
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                if (action.onClick) action.onClick(item);
                setDropdownOpen(false);
              }}
            >
              {action.icon && <i className={`${action.icon} me-2`}></i>}
              {action.label}
            </DropdownItem>
          ))}

          {onDelete && (
            <>
              <DropdownItem divider />
              <DropdownItem
                className="text-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(item);
                  setDropdownOpen(false);
                }}
              >
                <i className="ri-delete-bin-5-line text-danger me-2"></i>Delete
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </Dropdown>
    );
  };

  const MobileDataCard = ({
    item,
    columns,
    onCardClick,
    mobileActions = [],
  }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const handleCardClick = () => {
      if (onCardClick) {
        onCardClick(item);
      }
    };

    return (
      <Col xs={12} className="mb-3">
        <Card
          className="shadow-sm rounded-3 border-0 border-start border-4 border-primary"
          style={{ cursor: "pointer" }}
          onClick={handleCardClick}
        >
          <CardBody>
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                {columns.map((col, idx) => {
                  const colName = col.name?.toLowerCase();

                  if (colName === "s.n") return null;

                  if (
                    mobileActions.length > 0 &&
                    (colName.includes("action") || colName.includes("options"))
                  ) {
                    return null;
                  }

                  const value = col.selector
                    ? col.selector(item, 0)
                    : item[col.name] ?? "-";

                  if (col.cell) {
                    return <div key={idx}>{col.cell(item)}</div>;
                  }

                  return (
                    <p key={idx} className="mb-1 text-muted small">
                      <strong>{col.name}:</strong> {value ?? "-"}
                    </p>
                  );
                })}
              </div>

              {mobileActions.length > 0 && (
                <Dropdown
                  isOpen={dropdownOpen}
                  toggle={() => setDropdownOpen(!dropdownOpen)}
                >
                  <DropdownToggle
                    color="light"
                    className="btn-sm rounded-circle shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-more-2-fill" />
                  </DropdownToggle>
                  <DropdownMenu end>
                    {mobileActions.map((action, idx) => (
                      <DropdownItem
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick(item);
                          setDropdownOpen(false);
                        }}
                      >
                        {action.icon && <i className={`${action.icon} me-2`} />}
                        {action.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  };

  const DefaultTabletCard = ({
    item,
    onView,
    onEdit,
    onDelete,
    mobileActions = [],
    onCardClick,
  }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const handleCardClick = () => {
      if (onCardClick) {
        onCardClick(item);
      } else {
        onView?.(item);
      }
    };

    return (
      <Col xs={6} className="mb-3">
        <Card
          className="shadow-sm rounded-3 border-0 border-start border-4 border-success"
          style={{ cursor: "pointer" }}
          onClick={handleCardClick}
        >
          <CardBody>
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <h6 className="mb-2 fw-bold text-primary">
                  {item.firstName} {item.lastName}
                </h6>
                <p className="mb-1 text-muted small">
                  <i className="bx bx-envelope me-1" /> {item.email || "-"}
                </p>
                <p className="mb-2 text-muted small">
                  <i className="bx bx-phone me-1" /> {item.phoneNo || "-"}
                </p>
              </div>

              <Dropdown
                isOpen={dropdownOpen}
                toggle={() => setDropdownOpen(!dropdownOpen)}
              >
                <DropdownToggle
                  color="light"
                  className="btn-sm rounded-circle shadow-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="ri-more-2-fill" />
                </DropdownToggle>
                <DropdownMenu end>
                  {mobileActions.map((action, idx) => (
                    <DropdownItem
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick(item);
                        setDropdownOpen(false);
                      }}
                    >
                      {action.icon && <i className={`${action.icon} me-2`} />}
                      {action.label}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  };

  const handleSearchChange = (val) => {
    if (isServerSideSearch) {
      if (externalSearchTerm === undefined) setLocalSearchTerm(val);
      onSearch(val);
    } else {
      setInternalSearchTerm(val);
    }
  };

  const FilterSelectDropdown = () => {
    if (!onSelect || !selectOptions.length) return null;

    // Transform options for react-select format
    const formattedOptions = selectOptions.map((option) => ({
      value: option.value,
      label: option.label,
    }));

    // Find the selected option object
    const selectedOption =
      formattedOptions.find((option) => option.value === selectValue) || null;

    // Custom styles for react-select
    const customStyles = {
      control: (provided, state) => ({
        ...provided,
        minHeight: "38px",
        minWidth: "200px",
        borderRadius: "8px",
        borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
        boxShadow: state.isFocused
          ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
          : "none",
        "&:hover": {
          borderColor: "#86b7fe",
        },
        fontSize: "0.875rem",
      }),
      option: (provided, state) => ({
        ...provided,
        fontSize: "0.875rem",
        backgroundColor: state.isSelected
          ? "#0d6efd"
          : state.isFocused
          ? "#e7f1ff"
          : "white",
        color: state.isSelected ? "white" : "#212529",
        "&:active": {
          backgroundColor: "#0d6efd",
        },
      }),
      menu: (provided) => ({
        ...provided,
        zIndex: 9999,
      }),
      singleValue: (provided) => ({
        ...provided,
        fontSize: "0.875rem",
      }),
      placeholder: (provided) => ({
        ...provided,
        fontSize: "0.875rem",
        color: "#6c757d",
      }),
    };

    return (
      <div className="d-flex flex-column">
        {selectLabel && (
          <label className="form-label mb-1 small fw-semibold">
            {selectLabel}
          </label>
        )}
        <Select
          value={selectedOption}
          onChange={(option) => onSelect(option ? option.value : "")}
          options={formattedOptions}
          isClearable
          placeholder={selectPlaceholder}
          styles={customStyles}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>
    );
  };

  const actionColumn = [];

  // Only add Action column if any action is enabled
  const hasAnyAction =
    onView || onEdit || onDelete || (customActions && customActions.length > 0);

  if (hasAnyAction) {
    actionColumn.push({
      name: "Action",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "180px",
      center: true,
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-center">
          {/* View Button */}
          {onView && (
            <Button
              size="sm"
              color="soft-info"
              onClick={(e) => {
                e.stopPropagation();
                handleView(row);
              }}
              title="View"
            >
              <i className="bx bx-show" />
            </Button>
          )}

          {/* Edit Button - Only if onEdit exists */}
          {onEdit && (
            <Button
              size="sm"
              color="soft-warning"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
              title="Edit"
            >
              <i className="ri-edit-line" />
            </Button>
          )}

          {/* Custom Actions */}
          {customActions?.map((action, idx) => {
            // Optional: add visibility condition per row
            if (action.visible && !action.visible(row)) return null;

            return (
              <Button
                key={idx}
                size="sm"
                color={action.color || "soft-danger"}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(row);
                }}
                pointerOnHover
                title={action.label}
              >
                {action.icon ? <i className={action.icon} /> : action.label[0]}
              </Button>
            );
          })}

          {/* Delete Button */}
          {onDelete && (
            <Button
              size="sm"
              color="soft-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row);
              }}
              title="Delete"
            >
              <i className="ri-delete-bin-5-line" />
            </Button>
          )}
        </div>
      ),
    });
  }

  const StatsSection = showStats && stats.length > 0 && (
    <Row className="g-3">
      {isSmallScreen ? (
        <Col xs={12}>
          <Card className="shadow-sm border-0">
            <CardHeader
              style={{
                backgroundColor: "#f8f9fa",
                cursor: "pointer",
                borderBottom: "1px solid #eee",
              }}
              onClick={() => setIsStatsOpen(!isStatsOpen)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold text-dark">
                  <i className="bx bx-bar-chart-alt me-2"></i>Summary
                </h6>
                <i
                  className={`bx bx-chevron-${isStatsOpen ? "up" : "down"}`}
                ></i>
              </div>
            </CardHeader>
            <Collapse isOpen={isStatsOpen}>
              <CardBody>
                <Row className="g-3">
                  {stats.map((stat, idx) => (
                    <Col xs={6} key={idx}>
                      <MobileStatCard key={idx} {...stat} />
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Collapse>
          </Card>
        </Col>
      ) : window.innerWidth < 1024 ? (
        stats.map((stat, idx) => (
          <Col xs={12} sm={6} key={idx}>
            <StatCard {...stat} />
          </Col>
        ))
      ) : (
        stats.map((stat, idx) => (
          <Col xs={12} sm={6} md={3} key={idx}>
            <StatCard {...stat} />
          </Col>
        ))
      )}
    </Row>
  );

  const EmptyState = (
    <div className="text-center my-5 w-100">
      <i className={`${emptyStateIcon} display-4 text-secondary`} />
      <h5 className="fw-semibold text-secondary mt-3">No Data Found</h5>
      <p className="text-muted mb-0">
        {effectiveSearchTerm
          ? "No results match your search"
          : emptyStateMessage}
      </p>
    </div>
  );

  const LoadingState = (
    <div className="d-flex justify-content-center my-5">
      <Triangle visible height="80" width="80" color="#5B71B9" />
    </div>
  );

  // Server-side: use totalRows from API
  // Client-side: use filteredData.length
  const totalItems = totalRows || filteredData.length;
  const itemsPerPage = pageSize || 10;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const displayedData =
    totalRows > 0
      ? filteredData
      : filteredData.slice(
          (currentPage - 1) * pageSize,
          currentPage * pageSize
        );

  const hasMore = currentPage < totalPages;

  const loadMore = () => {
    const nextPage = currentPage + 1;
    const perPage = pageSize || 10;
    onPaginationChange?.({ page: nextPage, perPage });
  };

  if (isSmallScreen) {

    return (
      <Container fluid>
        <div className="page-content">
          {showBreadcrumb && <BreadCrumb title={title} pageTitle={pageTitle} />}
          {StatsSection}

          <div
            className="p-3 p-lg-4 mt-1"
            style={{
              background: "#ffffff",
              borderRadius: "0.5rem",
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              border: "1px solid #eee",
            }}
          >
            {/* Header */}
            <Row className="justify-content-between align-items-center mb-3">
              <Col xs={8}>
                <h5 className="mb-1 fw-bold text-dark">{mobileCardTitle}</h5>
              </Col>
              <Col xs={4} className="text-end">
                {mobileFilterButton}
              </Col>
            </Row>

            {/* Search */}
            {(showSearch || onSelect) && (
              <Row className="mb-3 g-2">
                {showSearch && (
                  <Col
                    xs="auto"
                    style={{ flex: "1 1 auto", minWidth: "200px" }}
                  >
                    <TableSearch
                      value={effectiveSearchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search..."
                    />
                  </Col>
                )}
                {onSelect && (
                  <Col xs="auto" style={{ minWidth: "200px" }}>
                    <FilterSelectDropdown />
                  </Col>
                )}
                {onAdd && (
                  <Col xs="auto">
                    <Button
                      color="success"
                      onClick={handleAdd}
                      className="d-flex align-items-center gap-2"
                      style={{ borderRadius: "8px", whiteSpace: "nowrap" }}
                    >
                      <i className="bx bx-plus"></i> Add
                    </Button>
                  </Col>
                )}
              </Row>
            )}

            {/* Content */}
            {loading && currentPage === 1 ? (
              LoadingState
            ) : filteredData?.length === 0 ? (
              EmptyState
            ) : (
              <>
                {/* Render Cards */}
                {displayedData.map((item, idx) =>
                  mobileCardRenderer ? (
                    mobileCardRenderer(item, idx)
                  ) : (
                    <MobileDataCard
                      key={item.id || idx}
                      item={item}
                      columns={columns}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                      mobileActions={customActions}
                      onCardClick={item.onClick}
                    />
                  )
                )}

                {/* Load More Button */}
                {hasMore && (
                  <div className="text-center mt-4 pb-2">
                    <Button
                      color="primary"
                      outline
                      onClick={loadMore}
                      disabled={loading}
                      className="px-5 shadow-sm"
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Loading...
                        </>
                      ) : (
                        "Load More"
                      )}
                    </Button>
                  </div>
                )}

                {/* Optional: Show count */}
                {!hasMore && filteredData.length > 0 && (
                  <div className="text-center text-muted small mt-3">
                    Showing all {totalItems} items
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Container>
    );
  }

  // Tablet View
  if (!isSmallScreen && window.innerWidth < 1024) {
    return (
      <Container fluid>
        <div className="page-content">
          {showBreadcrumb && <BreadCrumb title={title} pageTitle={pageTitle} />}
          {StatsSection}
          <div
            className="p-3 p-lg-4 mt-1"
            style={{
              background: "#ffffff",
              borderRadius: "0.5rem",
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              border: "1px solid #eee",
            }}
          >
            <Row className="justify-content-between align-items-center align-content-center mb-2">
              <Col md={4}>
                <h5 className="mb-1 fw-bold text-dark">{mobileCardTitle}</h5>
              </Col>
              <Col md={8} className="mb-3 mt-3">
                <Row className="g-2 justify-content-end">
                  {showSearch && (
                    <Col xs="auto">
                      <TableSearch
                        value={effectiveSearchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search..."
                      />
                    </Col>
                  )}
                  {onSelect && (
                    <Col xs="auto">
                      <FilterSelectDropdown />
                    </Col>
                  )}
                  {onAdd && (
                    <Col xs="auto">
                      <Button
                        color="success"
                        onClick={handleAdd}
                        className="d-flex align-items-center gap-2"
                        style={{ borderRadius: "8px" }}
                      >
                        <i className="bx bx-plus"></i> Add
                      </Button>
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>

            {loading ? (
              LoadingState
            ) : filteredData?.length === 0 ? (
              EmptyState
            ) : (
              <>
                <Row>
                  {displayedData.map((item, idx) => {
                    // Wrap the mobile card renderer in a Col for 2-column layout
                    const cardContent = mobileCardRenderer ? (
                      mobileCardRenderer(item, idx)
                    ) : (
                      <MobileDataCard
                        item={item}
                        columns={columns}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                        mobileActions={customActions}
                        onCardClick={item.onClick}
                      />
                    );

                    return (
                      <Col xs={12} sm={6} key={item.id || idx}>
                        {cardContent}
                      </Col>
                    );
                  })}
                </Row>

                {/* Load More Button for Tablet */}
                {hasMore && (
                  <div className="text-center mt-4 pb-2">
                    <Button
                      color="primary"
                      outline
                      onClick={loadMore}
                      disabled={loading}
                      className="px-5 shadow-sm"
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Loading...
                        </>
                      ) : (
                        "Load More"
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Container>
    );
  }

  // Desktop View
  return (
    <Container fluid>
      <div className="page-content">
        {showBreadcrumb && <BreadCrumb title={title} pageTitle={pageTitle} />}

        {StatsSection}
        <div
          className="p-3 p-lg-4 mt-1"
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          <Row className="justify-content-between align-items-center align-content-center mb-2">
            <Col md={4}>
              <h5 className="mb-1 fw-bold text-dark">{mobileCardTitle}</h5>
            </Col>
            <Col md={8}>
              <Row className="g-2 justify-content-end">
                {showSearch && (
                  <Col xs="auto" style={{ minWidth: "250px" }}>
                    <TableSearch
                      value={effectiveSearchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search..."
                    />
                  </Col>
                )}
                {onSelect && (
                  <Col xs="auto" style={{ minWidth: "200px" }}>
                    <FilterSelectDropdown />
                  </Col>
                )}
                {onAdd && (
                  <Col xs="auto">
                    <Button
                      color="success"
                      onClick={handleAdd}
                      className="d-flex align-items-center gap-2"
                      style={{ borderRadius: "8px" }}
                    >
                      <i className="bx bx-plus"></i> Add
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>

          {/* ✅ NEW: Custom header content (filters, etc.) */}
          {customHeaderContent && (
            <div className="mb-3">{customHeaderContent}</div>
          )}

          <hr style={{ borderColor: "#dee2e6", marginTop: "0rem" }} />

          {loading ? (
            LoadingState
          ) : filteredData?.length === 0 ? (
            EmptyState
          ) : (
              <CustomDataTable
                responsive
                striped
                pagination
                fixedHeader
                highlightOnHover
                pointerOnHover
                persistTableHead
                customStyles={tableStyles}
                progressPending={loading}
                progressComponent={LoadingState}
                paginationServer={Boolean(totalRows)}
                paginationTotalRows={
                  totalRows > 0 ? totalRows : filteredData.length
                }
                paginationDefaultPage={currentPage}
                onChangePage={handlePaginationChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
                columns={[...columns, ...actionColumn]}
                data={filteredData}
                paginationPerPage={pageSize}
                noDataComponent={EmptyState}
              />
          )}

          <DeleteModal
            show={deleteModal}
            onDeleteClick={handleDeleteConfirm}
            onCloseClick={() => {
              setDeleteModal(false);
              setSelectedItem(null);
            }}
            selectedItem={selectedItem}
          />
        </div>
      </div>
    </Container>
  );
};

ResponsiveDataManagement.propTypes = {
  title: PropTypes.string,
  pageTitle: PropTypes.string,
  totalRows: PropTypes.number,
  mobileCardTitle: PropTypes.string,
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  stats: PropTypes.array,
  searchKeys: PropTypes.array,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onView: PropTypes.func,
  onDelete: PropTypes.func,
  showStats: PropTypes.bool,
  statsCollapsible: PropTypes.bool,
  loading: PropTypes.bool,
  mobileCardRenderer: PropTypes.func,
  tabletCardRenderer: PropTypes.func,
  customActions: PropTypes.array,
  emptyStateMessage: PropTypes.string,
  emptyStateIcon: PropTypes.string,
  showBreadcrumb: PropTypes.bool,
  showSearch: PropTypes.bool,
  customStyles: PropTypes.object,
  onPaginationChange: PropTypes.func,
  onSearch: PropTypes.func,
  searchTerm: PropTypes.string,
  customHeaderContent: PropTypes.node,
  mobileFilterButton: PropTypes.node,
  onSelect: PropTypes.func,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
};

ResponsiveDataManagement.defaultProps = {
  stats: [],
  searchKeys: [],
  showStats: true,
  statsCollapsible: true,
  loading: false,
  customActions: [],
  emptyStateMessage: "No data found",
  emptyStateIcon: "ri-emotion-unhappy-line",
  showBreadcrumb: true,
  showSearch: true,
  customStyles: {},
  onSearch: undefined,
  searchTerm: undefined,
  customHeaderContent: null,
  mobileFilterButton: null,
  totalRows: 0,
  onSelect: null,
};

export default ResponsiveDataManagement;
